import React, { useState, useEffect } from "react";
import { Box, Text, useColorModeValue } from "@chakra-ui/react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { fetchPosts } from "../github";
import About from "./About";
import ActiveProjects from "../components/ActiveProjects";

const JF = "'Josefin Sans', sans-serif";
const PJ = "'Plus Jakarta Sans', sans-serif";

const PLACEHOLDERS = [
  "linear-gradient(145deg,#0c1520 0%,#1a2d44 100%)",
  "linear-gradient(145deg,#120a1a 0%,#2a1040 100%)",
  "linear-gradient(145deg,#0a1a0a 0%,#142814 100%)",
];

// ── Latest Posts ───────────────────────────────────────────────
function RecentPosts({ articles }) {
  const labelColor   = useColorModeValue("#888888", "#aaaaaa");
  const dividerColor = useColorModeValue("#e0e0e0", "#2a2a2a");
  const titleColor   = useColorModeValue("#0c0c0c", "#f0f0f0");
  const titleHover   = useColorModeValue("#444444", "#aaaaaa");
  const descColor    = useColorModeValue("#555555", "#999999");
  const btnBorder    = useColorModeValue("#cccccc", "#444444");
  const btnColor     = useColorModeValue("#444444", "#aaaaaa");
  const btnHoverBg   = useColorModeValue("#f0f0f0", "#2a2a2a");
  const ctaColor     = useColorModeValue("#aaaaaa", "#888888");

  const recent = articles.slice(0, 3);
  if (recent.length === 0) return null;

  return (
    <Box as="section" pt={14} pb={12}>

      <Text
        fontFamily={JF}
        fontSize="13px"
        fontWeight="700"
        letterSpacing="0.2em"
        textTransform="uppercase"
        color={labelColor}
        mb={6}
      >
        Latest Posts
      </Text>

      {recent.map((post, idx) => {
        const { title, description, headerImage } = post.fields;
        const slug = post.slug;
        const placeholder = PLACEHOLDERS[idx % PLACEHOLDERS.length];
        const dateStr = new Date(post.sys.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

        return (
          <React.Fragment key={post.sys.id}>
            <Box h="1px" bg={dividerColor} />
            <Link to={`/posts/${slug}`} style={{ textDecoration: "none" }}>
              <Box
                display="flex"
                alignItems="flex-start"
                justifyContent="space-between"
                gap="24px"
                py={8}
                sx={{
                  "&:hover .lp-title": { color: titleHover },
                  "&:hover .lp-btn":   { bg: btnHoverBg },
                }}
              >
                {/* Left: text */}
                <Box flex="1" minW={0}>
                  <Text
                    fontFamily={JF}
                    fontSize="11px"
                    letterSpacing="0.08em"
                    color={descColor}
                    mb={2}
                  >
                    {dateStr}
                  </Text>
                  <Text
                    className="lp-title"
                    fontFamily={PJ}
                    fontSize="20px"
                    fontWeight="700"
                    color={titleColor}
                    lineHeight={1.35}
                    mb={5}
                    transition="color 0.2s ease"
                  >
                    {title}
                  </Text>

                  {description && (
                    <Text
                      fontFamily={PJ}
                      fontSize="14px"
                      color={descColor}
                      lineHeight="1.7"
                      mb={5}
                      sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
                    >
                      {description}
                    </Text>
                  )}

                  <Box
                    className="lp-btn"
                    as="span"
                    display="inline-block"
                    border="1px solid"
                    borderColor={btnBorder}
                    borderRadius="3px"
                    px="12px"
                    py="5px"
                    fontFamily={JF}
                    fontSize="11px"
                    fontWeight="600"
                    letterSpacing="0.14em"
                    textTransform="uppercase"
                    color={btnColor}
                    transition="background 0.2s ease"
                  >
                    More +
                  </Box>
                </Box>

                {/* Right: image */}
                <Box
                  flexShrink={0}
                  w={{ base: "120px", sm: "180px" }}
                  h={{ base: "100px", sm: "150px" }}
                  borderRadius="4px"
                  position="relative"
                  style={{ overflow: "hidden", background: placeholder }}
                >
                  {headerImage && (
                    <Box
                      as="img"
                      src={headerImage}
                      alt={title}
                      w="100%"
                      h="100%"
                      display="block"
                      style={{ objectFit: "contain" }}
                    />
                  )}
                </Box>
              </Box>
            </Link>
          </React.Fragment>
        );
      })}

      <Box h="1px" bg={dividerColor} mb={8} />

      <Link to="/posts">
        <Text
          fontFamily={JF}
          fontSize="12px"
          fontWeight="600"
          letterSpacing="0.2em"
          textTransform="uppercase"
          color={ctaColor}
          display="inline-block"
          sx={{ "&:hover": { letterSpacing: "0.26em" } }}
          transition="letter-spacing 0.2s ease"
        >
          View all posts →
        </Text>
      </Link>
    </Box>
  );
}

// ── Home ──────────────────────────────────────────────────────
export default function Home() {
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    fetchPosts().then(setArticles).catch(console.error);
  }, []);

  return (
    <>
      <Helmet>
        <title>Huy Phu | Security &amp; QA Engineer</title>
        <meta name="description" content="Personal site of Huy Phu — Senior QA Engineer and Security Engineer based in Los Angeles, CA. Writing about security, testing, and AI." />
        <meta property="og:title" content="Huy Phu | Security & QA Engineer" />
        <meta property="og:description" content="Personal site of Huy Phu — Senior QA Engineer and Security Engineer based in Los Angeles, CA." />
        <meta property="og:url" content="https://hqphu.com/" />
      </Helmet>
      <About />
      <ActiveProjects />
      <RecentPosts articles={articles} />
    </>
  );
}
